/**
 * AudioLevelMeter — TASK-420
 *
 * Single Responsibility: render an instant-feedback waveform of the
 * microphone stream so the advisor sees the system is "listening" while
 * the 30s chunk pipeline (MediaRecorder → /chunk → Whisper) catches up.
 *
 * Per §11.1 of MEETING-FLOW-AND-TRANSCRIPTION.md: the waveform updates at
 * the AnalyserNode tick rate (effectively requestAnimationFrame), which is
 * what makes the cockpit feel "live" even though transcription itself is
 * batched.
 *
 * Lifecycle:
 *   - Mounts an `AnalyserNode` on the passed `MediaStream` only.
 *   - Pauses draws when the tab is hidden (browsers throttle rAF to 1Hz
 *     in background tabs; pausing avoids redundant work).
 *   - Closes its `AudioContext` and disconnects the analyser on unmount.
 *
 * Reuses no existing component — there is no audio visualizer elsewhere
 * in the frontend (verified).
 */

import React, { useEffect, useRef } from 'react'

interface AudioLevelMeterProps {
  /** Microphone stream from `navigator.mediaDevices.getUserMedia`. */
  mediaStream: MediaStream | null
  /** Visual width in px (defaults to 120). */
  width?: number
  /** Visual height in px (defaults to 28). */
  height?: number
  /** Tailwind class for the bar color (defaults to a soft green). */
  className?: string
}

const AudioLevelMeter: React.FC<AudioLevelMeterProps> = ({
  mediaStream,
  width = 120,
  height = 28,
  className = '',
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    if (!mediaStream) return
    const canvas = canvasRef.current
    if (!canvas) return

    const audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()
    // Autoplay policy: AudioContext starts suspended until a user gesture.
    // The "Start Meeting" button that creates `mediaStream` is itself a user
    // gesture, so resume() is safe here.
    audioContext.resume().catch(() => {})

    const source = audioContext.createMediaStreamSource(mediaStream)
    const analyser = audioContext.createAnalyser()
    analyser.fftSize = 512
    source.connect(analyser)

    const buffer = new Uint8Array(analyser.fftSize)
    const ctx = canvas.getContext('2d')
    let rafId: number | null = null
    let stopped = false

    const draw = () => {
      if (stopped) return
      if (document.visibilityState !== 'visible') {
        // Browsers throttle rAF in background tabs; bail and let the
        // visibility-change listener restart the loop on resume.
        rafId = null
        return
      }
      analyser.getByteTimeDomainData(buffer)
      if (!ctx) {
        rafId = requestAnimationFrame(draw)
        return
      }
      ctx.clearRect(0, 0, width, height)
      ctx.lineWidth = 2
      ctx.strokeStyle = 'rgba(34, 197, 94, 0.85)' // tailwind green-500 @ 85%
      ctx.beginPath()
      const sliceWidth = width / buffer.length
      let x = 0
      for (let i = 0; i < buffer.length; i++) {
        const v = buffer[i] / 128.0 // 0..2
        const y = (v * height) / 2
        if (i === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
        x += sliceWidth
      }
      ctx.lineTo(width, height / 2)
      ctx.stroke()
      rafId = requestAnimationFrame(draw)
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && rafId === null && !stopped) {
        rafId = requestAnimationFrame(draw)
      }
    }
    document.addEventListener('visibilitychange', onVisibility)

    rafId = requestAnimationFrame(draw)

    return () => {
      stopped = true
      if (rafId !== null) cancelAnimationFrame(rafId)
      document.removeEventListener('visibilitychange', onVisibility)
      try {
        source.disconnect()
        analyser.disconnect()
      } catch {
        // already disconnected
      }
      audioContext.close().catch(() => {})
    }
  }, [mediaStream, width, height])

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      aria-label="Microphone audio level"
      role="img"
      className={className}
    />
  )
}

export default AudioLevelMeter
